import { isWithinOperatingHours } from '@conversia/shared-types';
import type { AgentSchedule } from '@/api/settings.api';
import type { ConversationStatus } from '@conversia/shared-types';
import { isBotActive } from '@/lib/bot-control';

/**
 * Estado del agente AI para el indicador de la conversación — lógica PURA.
 * Refleja el estado REAL del pipeline (misma lógica de horario que el motor,
 * compartida vía shared-types; la pausa espeja RESPONDABLE_STATUSES; el
 * "respondiendo" viene del evento agent.turn del backend). Nunca inventa.
 */

export type AgentStatusKind =
  | 'sin-agente'
  | 'pausa'
  | 'respondiendo'
  | 'fuera-horario'
  | 'disponible';

export interface AgentStatusView {
  kind: AgentStatusKind;
  label: string;
  /** gris = no va a responder; verde = puede; azul = respondiendo YA. */
  tone: 'gray' | 'green' | 'blue';
}

export function agentStatusFrom(input: {
  conversationStatus: ConversationStatus;
  hasAgent: boolean;
  schedule: AgentSchedule | undefined;
  responding: boolean;
  now?: Date;
}): AgentStatusView {
  if (!input.hasAgent) return { kind: 'sin-agente', label: 'Sin agente', tone: 'gray' };
  if (!isBotActive(input.conversationStatus)) {
    // «Lo atiende una persona» dice lo que pasa; «Bot en pausa» dice lo que NO
    // pasa, y deja al que mira preguntándose si eso está bien o está roto.
    return { kind: 'pausa', label: 'Lo atiende una persona', tone: 'gray' };
  }
  // Un turno EN VUELO es el estado más real de todos: gana aunque el horario
  // acabe de cerrarse (el turno ya arrancó).
  if (input.responding) return { kind: 'respondiendo', label: 'Respondiendo…', tone: 'blue' };
  const s = input.schedule;
  if (
    s &&
    s.horario_activo &&
    !isWithinOperatingHours(input.now ?? new Date(), s.timezone, s.responder_desde, s.responder_hasta)
  ) {
    return { kind: 'fuera-horario', label: 'Fuera de horario', tone: 'gray' };
  }
  // «ACTIVO», no «Disponible». Kervens (24-sep-2026): «cuando el asistente
  // habla debe aparecer con el botón verde diciendo activo». «Disponible» dice
  // que PODRÍA responder; quien mira el chat necesita saber quién lo está
  // atendiendo AHORA — si es la asistente o si hay que meterse.
  return { kind: 'disponible', label: 'Activo', tone: 'green' };
}
